<template>
  <div
    class="c-outreach-contact-twitter-engagement-metrics"
    data-testid="outreach-contact-twitter-engagement-metrics"
  >
    <QSpinner
      v-if="loading"
      size="15"
    />
    <template v-else>
      <a
        class="c-outreach-contact-twitter-engagement-metrics__username"
        :href="twitterUrl()"
        rel="noopener noreferrer"
        target="_blank"
      >
        @{{ username }}
      </a>

      <a
        v-for="stat in stats"
        :key="stat.text"
        class="c-outreach-contact-twitter-engagement-metrics__stat"
        :href="stat.href"
        rel="noopener noreferrer"
        target="_blank"
      >
        {{ stat.value }}
        <span class="c-outreach-contact-twitter-engagement-metrics__stat-label">
          {{ stat.text }}
        </span>
      </a>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocalePluralText, getLocaleText } from "shared/boot/i18n";
import { prettyNumber } from "shared/helpers/number";

const props = defineProps({
  socialIntegrationId: {
    type: String,
    required: true,
  },
  twitterUsername: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["set-twitter-user-id", "set-twitter-handle"]);

const loading = ref(false);
const data = ref();

const username = computed(() => data.value?.username);
const tweetCount = computed(() => data.value?.tweet_count);
const followersCount = computed(() => data.value?.followers_count);
const followingCount = computed(() => data.value?.following_count);

function twitterUrl(type = "") {
  return `https://twitter.com/${props.twitterUsername}/${type}`;
}

const stats = computed(() => [
  {
    text: getLocalePluralText(
      "outreach_contact_twitter_engagement_metrics.followers",
      followersCount.value
    ),
    value: prettyNumber(followersCount.value),
    href: twitterUrl("followers"),
  },
  {
    text: getLocaleText(
      "outreach_contact_twitter_engagement_metrics.following"
    ),
    value: prettyNumber(followingCount.value),
    href: twitterUrl("following"),
  },
  {
    id: "tweets",
    text: getLocalePluralText(
      "outreach_contact_twitter_engagement_metrics.tweets",
      tweetCount.value
    ),
    value: prettyNumber(tweetCount.value),
    href: twitterUrl(),
  },
]);

async function twitterUserLookup() {
  try {
    loading.value = true;

    data.value = (
      await $streemApiV1.get("social_integrations/twitter/user_lookup", {
        params: {
          social_integration_id: props.socialIntegrationId,
          twitter_username: props.twitterUsername,
        },
      })
    ).data;

    emit("set-twitter-user-id", data.value.id);
    emit("set-twitter-handle", data.value.username);
  } catch (error) {
    ErrorDialog(
      getLocaleText(
        "outreach_contact_twitter_engagement_metrics.user_lookup_error"
      ),
      error
    );
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  twitterUserLookup();
});
</script>

<style scoped lang="scss">
.c-outreach-contact-twitter-engagement-metrics {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s-spacing-md);
  font-weight: bold;
  font-size: var(--s-font-size-md);
  line-height: 150%;

  &__stat {
    cursor: pointer;
    color: var(--s-color-denim-9);

    &-label {
      color: var(--s-color-denim-7);

      &:hover {
        color: var(--s-color-denim-9);
      }
    }
  }

  &__username {
    cursor: pointer;
    color: var(--s-color-blue-9);

    &:hover {
      color: var(--s-color-blue-5);
    }
  }
}
</style>
